export type UsePointerProps<E = HTMLElement> = {
    id: number;
    x: number;
    y: number;
    offset: {
        x: number;
        y: number;
    };
    start: {
        x: number;
        y: number;
    };
    target: E;
    tgtOffset: {
        x: number;
        y: number;
    };
};
type UsePointerCallback = (args: UsePointerProps, ev: PointerEvent) => void;
type UsePointerArguments = [handler: UsePointerCallback, pointerDownEvent?: PointerEvent, options?: UsePointerOptions];
type UsePointerOptions = {
    pointCapture: boolean;
};
const UsePointerOptionDefaults = {
    pointCapture: true,
};
export class UsePointer {
    // vectors
    x = 0;
    y = 0;
    start = { x: 0, y: 0 };
    offset = { x: 0, y: 0 };

    target: HTMLElement | null = null;
    pointCapture = false;

    subscribers: {
        down: UsePointerCallback[];
        move: UsePointerCallback[];
        up: UsePointerCallback[];
    } = {
        down: [],
        move: [],
        up: [],
    };

    state = {
        down: false,
        move: false,
        up: true,
    };

    inputThreshold = 600;
    clickTS = 0;
    clickCount = 0;
    pressTS = 0;
    pressCount = 0;

    view: EventTarget = window;

    constructor(view?: EventTarget, inputThreshold?: number) {
        this.init(view, inputThreshold);
    }

    init(view?: EventTarget, inputThreshold?: number) {
        if (view) this.view = view;

        if (inputThreshold) this.inputThreshold = inputThreshold;

        // this.view.addEventListener('pointerdown', this.#downHandler)
    }

    dispose() {
        this.subscribers.down = [];

        this.view.removeEventListener('pointerdown', this.#downHandler);
        this.#removeEffectListeners();
    }

    // registration methods
    #register(
        subscribers: UsePointerCallback[],
        handler: UsePointerCallback,
        pointerDownEvent?: PointerEvent,
        { pointCapture }: UsePointerOptions = UsePointerOptionDefaults
    ) {
        subscribers.push(handler);
        this.pointCapture = pointCapture;

        if (!this.state.down && !this.state.move && pointerDownEvent) this.#downHandler(pointerDownEvent);
    }
    down(...args: UsePointerArguments) {
        this.#register(this.subscribers.down, ...args);
    }

    move(...args: UsePointerArguments) {
        this.#register(this.subscribers.move, ...args);
    }

    up(...args: UsePointerArguments) {
        this.#register(this.subscribers.up, ...args);
    }

    #removeEffectListeners() {
        this.view.removeEventListener('pointermove', this.#moveHandler);
        this.view.removeEventListener('pointerup', this.#stopHandler);
        this.view.removeEventListener('pointercancel', this.#stopHandler);
    }

    #downHandler: EventListener = (event: Event) => {
        let ev: PointerEvent = event as PointerEvent,
            evt = ev.target as HTMLElement;

        if (evt && this.pointCapture) evt.setPointerCapture(ev.pointerId);

        this.state.down = true;
        this.state.up = false;

        this.target = evt;
        this.start.x = this.x = ev.x;
        this.start.y = this.y = ev.y;
        this.offset.x = 0;
        this.offset.y = 0;

        this.thresholdCounter('pressTS', 'pressCount');

        this.#emit(this.subscribers.down, ev);

        if (this.subscribers.move.length) this.view.addEventListener('pointermove', this.#moveHandler);
        this.view.addEventListener('pointerup', this.#stopHandler);
        this.view.addEventListener('pointercancel', this.#stopHandler);
    };

    #moveHandler: EventListener = (event: Event) => {
        let ev: PointerEvent = event as PointerEvent;

        this.state.move ||= true;

        this.x = ev.x;
        this.y = ev.y;
        this.offset.x = ev.x - this.start.x;
        this.offset.y = ev.y - this.start.y;

        this.#emit(this.subscribers.move, ev);
    };

    thresholdCounter(tsProp: ('pressTS' | 'clickTS') & string, countProp: ('pressCount' | 'clickCount') & string) {
        // count input withing threshold
        let ts = Date.now();
        if (ts - this[tsProp] > this.inputThreshold) {
            this[countProp] = 0;
            this[tsProp] = 0;
        }

        if (!this[tsProp] || ts - this[tsProp] < this.inputThreshold) ++this[countProp];

        this[tsProp] = ts;
    }

    #stopHandler: EventListener = (event: Event) => {
        let ev: PointerEvent = event as PointerEvent;

        this.target?.releasePointerCapture(ev.pointerId);
        this.pointCapture &&= false;

        this.state.down = this.state.move = false;
        this.state.up = true;

        this.thresholdCounter('clickTS', 'clickCount');

        this.#emit(this.subscribers.up, ev);

        this.subscribers.move = [];
        this.subscribers.up = [];

        this.#removeEffectListeners();
    };

    #emit(subscribers: UsePointerCallback[], ev: PointerEvent) {
        for (let cb of subscribers)
            cb(
                {
                    id: ev.pointerId,
                    x: this.x,
                    y: this.y,
                    start: this.start,
                    offset: this.offset,
                    target: this.target as HTMLElement,
                    tgtOffset: {
                        x: ev.offsetX,
                        y: ev.offsetY,
                    },
                },
                ev
            );
    }
}

export const usePointer = new UsePointer();
